import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import unitService from '../../services/unitService';
import './ProductCategoryListPage.css';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2 } from 'lucide-react';

const UnitListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await unitService.getUnits(1, 100);
      if (response?.data) {
        setItems(response.data);
      }
      setLoading(false);
    } catch (err) {
      setError(t('unit.failedToLoadUnit'));
      setLoading(false);
    }
  };

  const handleDelete = (id: number) => {
    navigate(`/products/units/${id}/delete`);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await unitService.getUnits(1, 100);
      if (response?.data) {
        setItems(response.data);
      }
    } catch (err) {
      setError(t('unit.failedToLoadUnit'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'unitName',
      label: t('unit.unitName'),
      sortable: true,
      width: '220px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'unitShortName',
      label: t('unit.abbreviation'),
      sortable: true,
      width: '150px',
      render: (value) => <span className="description-text">{value}</span>,
    },
    {
      key: 'description',
      label: t('unit.description'),
      sortable: true,
      width: '280px',
      hideTablet: true,
      hideMobile: true,
      render: (value) => <span className="description-text">{value || t('common.noDescription')}</span>,
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/products/units/${item.unitId}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    {
      icon: (
        <SquarePen size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/products/units/${item.unitId}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
    {
      icon: (
        <Trash2 size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => handleDelete(item.unitId),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    },
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('unit.title')}</h1>
          <div className="header-actions">
            <button
              className="btn-new"
              onClick={() => navigate('/products/units/create')}
              title={t('unit.newUnit')}
            >
              <CirclePlus/>
              {t('unit.newUnit')}
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('unit.refreshUnits')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('unit.refreshUnits')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={items}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('unit.noUnitsFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default UnitListPage;
